// src/components/AdminRoute.jsx
import { useState, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import { api, apiPaths, API_TIMEOUT_DEFAULT } from "../config/api";
import LoadingSpinner from "./LoadingSpinner";

// Short-circuit key + TTL: if the admin stats check succeeded recently,
// skip the network call on subsequent navigations to avoid re-mounting children.
const ADMIN_AUTH_KEY = 'admin_auth_ts';

const markVerified = () => {
  try { sessionStorage.setItem(ADMIN_AUTH_KEY, String(Date.now())); } catch {}
};

const clearVerified = () => {
  try { sessionStorage.removeItem(ADMIN_AUTH_KEY); } catch {}
};

const AdminRoute = ({ children }) => {
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [authError, setAuthError] = useState("");
  const [accessDeniedRedirect, setAccessDeniedRedirect] = useState(null);
  const retryRef = useRef(0);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    const MAX_RETRIES = 3;

    const checkToken = async () => {
      try {
        setAuthError("");
        await api.get(apiPaths.admin.stats(), { timeout: API_TIMEOUT_DEFAULT });
        if (!isMounted.current) return;
        markVerified();
        setIsAuthorized(true);
      } catch (err) {
        if (!isMounted.current) return;

        if (err.response?.status === 403) {
          // Logged-in customer (or non-admin) — send to home, not admin login
          clearVerified();
          setIsAuthorized(false);
          setAccessDeniedRedirect('/');
        } else if (err.response?.status === 401) {
          clearVerified();
          setIsAuthorized(false);
          setAccessDeniedRedirect('/admin/login');
        } else if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK' || !err.response) {
          // Network/timeout error — retry with backoff, but do NOT re-trigger the effect.
          if (retryRef.current < MAX_RETRIES) {
            retryRef.current += 1;
            if (isMounted.current) {
              setAuthError(`Verifying... (retry ${retryRef.current}/${MAX_RETRIES})`);
            }
            const delay = 2000 * retryRef.current;
            setTimeout(() => {
              if (isMounted.current) checkToken();
            }, delay);
          } else {
            clearVerified();
            setAuthError("Unable to verify admin session. Please check your connection and try again.");
            setIsAuthorized(false);
          }
        } else {
          clearVerified();
          setAuthError("Authentication check failed. Please login again.");
          setIsAuthorized(false);
        }
      }
    };

    checkToken();

    return () => {
      isMounted.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount; retries are handled internally via setTimeout.

  // Loading state while verifying admin session with the server
  if (isAuthorized === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <LoadingSpinner size="md" variant="purple" className="mx-auto mb-4" />
          <div className="text-gray-600">
            {authError || "Verifying authentication..."}
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthorized && authError) {
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <div className="max-w-md text-center">
          <div className="text-gray-800 font-semibold mb-2">Admin access required</div>
          <div className="text-gray-600 mb-6">{authError}</div>
          <button
            type="button"
            onClick={() => (window.location.href = "/admin/login")}
            className="px-4 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700"
          >
            Go to login
          </button>
        </div>
      </div>
    );
  }

  if (accessDeniedRedirect) {
    return <Navigate to={accessDeniedRedirect} replace />;
  }

  return isAuthorized ? children : <Navigate to="/admin/login" replace />;
};

export default AdminRoute;

